import { Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminIntegrationVisibilityDetail, type AdminOrganizationRow } from '@/api/adminClient';
import { OrganizationPicker, organizationName } from '@/components/org/OrganizationPicker';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { formatDate } from '@/lib/format';
import { VISIBILITY_OPTIONS, visibilitySummary } from '@/lib/integrationVisibility';

function message(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function OrganizationRows({
  label,
  organizations,
  busy,
  onRemove,
}: {
  label: string;
  organizations: Array<{ organizationId: string; name: string; assignedBy?: string; assignedAt?: string | null }>;
  busy: boolean;
  onRemove: (organization: { organizationId: string; name: string }) => void;
}) {
  return (
    <ul aria-label={label} className="divide-y divide-border rounded-lg border border-border">
      {organizations.map((organization) => (
        <li key={organization.organizationId} className="flex items-center justify-between gap-3 px-3 py-2">
          <span className="min-w-0">
            <span className="block truncate text-sm text-foreground">{organization.name}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {organization.organizationId}
              {organization.assignedBy ? ` · added by ${organization.assignedBy}` : ''}
              {organization.assignedAt ? ` on ${formatDate(organization.assignedAt)}` : ''}
            </span>
          </span>
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onRemove(organization)} aria-label={`Remove ${organization.name}`}>
            <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Remove
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Who can use one integration: everyone, or only the organizations assigned to it. An organization's own
 * connector and one the API has not converted yet are shown, never changed. A connector also lists the
 * organizations that get its version published for testing.
 */
export function IntegrationVisibilityEditor({
  detail,
  organizations,
  onSaved,
}: {
  detail: AdminIntegrationVisibilityDetail;
  organizations: AdminOrganizationRow[];
  onSaved: (next: AdminIntegrationVisibilityDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const nameOf = (organizationId: string) => organizationName(organizations, organizationId);
  const summary = visibilitySummary({ ...detail, organizationCount: detail.organizations.length }, nameOf);
  const editable = detail.visibility === 'everyone' || detail.visibility === 'assigned';
  const testing = detail.testingOrganizations ?? [];

  const run = async (change: () => Promise<AdminIntegrationVisibilityDetail>, success: string, fallback: string) => {
    setBusy(true);
    setError(undefined);
    try {
      onSaved(await change());
      toast.success(success);
    } catch (err) {
      setError(message(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const choose = (visibility: 'everyone' | 'assigned') => {
    if (visibility === detail.visibility) return;
    if (visibility === 'everyone' && !window.confirm(`Open ${detail.title} to every organization? It is on for them unless they turn it off.`)) return;
    void run(
      () => adminClient.setIntegrationVisibility(detail.integrationId, visibility),
      visibility === 'everyone' ? 'Every organization can use it now' : 'Only the listed organizations can use it now',
      'Failed to change who can use it',
    );
  };

  const setTesting = (organizationIds: string[], success: string) =>
    void run(() => adminClient.setIntegrationTestingOrganizations(detail.integrationId, organizationIds), success, 'Failed to change the testing organizations');

  return (
    <div className="space-y-6">
      <AdminSection
        title="Who can use this"
        description="Organizations that can use it find it in their catalog. It is on for them unless they turn it off."
        actions={<StatusPill tone={summary.tone}>{summary.label}</StatusPill>}
      >
        {detail.visibility === null ? (
          <p className="text-sm text-muted-foreground">
            Studio is moving this integration to organization access. It takes a few minutes, and nothing changes for the organizations using it
            meanwhile.
          </p>
        ) : detail.visibility === 'organization' ? (
          <p className="text-sm text-muted-foreground">
            {detail.ownerOrganizationId ? nameOf(detail.ownerOrganizationId) : 'One organization'} built this connector, so only it can use it.
            Who can use it is not changed here.
          </p>
        ) : (
          <div className="space-y-4">
            <fieldset className="space-y-2" disabled={busy}>
              <legend className="sr-only">Who can use this</legend>
              {VISIBILITY_OPTIONS.map((option) => (
                <label key={option.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border px-3 py-2.5 hover:bg-muted/40">
                  <input
                    type="radio"
                    name={`visibility-${detail.integrationId}`}
                    value={option.id}
                    checked={detail.visibility === option.id}
                    onChange={() => choose(option.id)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.description}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            {detail.visibility === 'assigned' ? (
              <div className="space-y-3">
                {detail.organizations.length > 0 ? (
                  <OrganizationRows
                    label="Assigned organizations"
                    organizations={detail.organizations}
                    busy={busy}
                    onRemove={(organization) =>
                      void run(
                        () => adminClient.unassignIntegration(detail.integrationId, organization.organizationId),
                        `${organization.name} no longer has it`,
                        'Failed to remove the organization',
                      )
                    }
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">No organization has it yet. Add one below.</p>
                )}
                <OrganizationPicker
                  label="Add an organization"
                  organizations={organizations}
                  exclude={detail.organizations.map((organization) => organization.organizationId)}
                  disabled={busy}
                  onPick={(organization) =>
                    void run(
                      () => adminClient.assignIntegration(detail.integrationId, organization.organizationId),
                      `${organization.name} has it now`,
                      'Failed to add the organization',
                    )
                  }
                />
              </div>
            ) : detail.organizations.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                {detail.organizations.length === 1 ? '1 organization stays' : `${detail.organizations.length} organizations stay`} assigned. Switch
                back to Specific organizations and only they have it again.
              </p>
            ) : null}
          </div>
        )}
        {editable && busy ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Saving
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </AdminSection>

      {detail.kind === 'connector' ? (
        <AdminSection
          title="Testing organizations"
          description="These organizations get the version published for testing in place of the released one. Everyone else keeps the released version."
        >
          <div className="space-y-3">
            {testing.length > 0 ? (
              <OrganizationRows
                label="Testing organizations"
                organizations={testing}
                busy={busy}
                onRemove={(organization) =>
                  setTesting(
                    testing.filter((entry) => entry.organizationId !== organization.organizationId).map((entry) => entry.organizationId),
                    `${organization.name} no longer tests it`,
                  )
                }
              />
            ) : (
              <p className="text-xs text-muted-foreground">None yet. Publishing for testing then uses the publisher&apos;s organization.</p>
            )}
            <OrganizationPicker
              label="Add a testing organization"
              organizations={organizations}
              exclude={testing.map((organization) => organization.organizationId)}
              disabled={busy}
              onPick={(organization) =>
                setTesting([...testing.map((entry) => entry.organizationId), organization.organizationId], `${organization.name} tests it now`)
              }
            />
          </div>
        </AdminSection>
      ) : null}
    </div>
  );
}

/** Loads who can use one integration and lets a Studio admin change it. */
export function IntegrationVisibilityControl({ integrationId }: { integrationId: string }) {
  const [detail, setDetail] = useState<AdminIntegrationVisibilityDetail>();
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[]>([]);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setDetail(undefined);
    setError(undefined);
    Promise.all([adminClient.getIntegrationVisibility(integrationId), adminClient.listOrganizations()])
      .then(([next, list]) => {
        if (cancelled) return;
        setDetail(next);
        setOrganizations(list.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(message(err, 'Failed to load who can use this integration'));
      });
    return () => {
      cancelled = true;
    };
  }, [integrationId, reloadKey]);

  if (error) return <LoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} className="rounded-xl border border-border" />;
  if (!detail) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading who can use it
      </div>
    );
  }
  return <IntegrationVisibilityEditor detail={detail} organizations={organizations} onSaved={setDetail} />;
}
